export type LedgerSortRow = {
  id: number;
  posted_account: string;
  product?: string;
  posted_date: string | null;
  debit_amount: number | null;
  credit_amount: number | null;
  fee?: number | null;
  balance: number | null;
  clock?: string | null;
};

const BALANCE_EPS = 0.015;

export function formatDayMonthYear(value: string | null | undefined): string {
  if (!value) return '—';
  const [year, month, day] = value.split(/[T ]/, 1)[0].split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

/** Parse AIB credit-card "HH:MM, DD/MM/YYYY" (stored in description3). */
export function parseAibTransactionClock(description3: string | null | undefined): string | null {
  const match = String(description3 || '').match(
    /^(\d{1,2}):(\d{2})(?::\d{2})?,\s*\d{2}\/\d{2}\/\d{4}/
  );
  if (!match) return null;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

export function clockFromIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const time = value.includes('T') ? value.split('T')[1] : value.split(' ')[1];
  if (!time) return null;
  return time.slice(0, 5);
}

export function formatPostedDateTime(
  postedDate: string | null | undefined,
  clock: string | null | undefined
): string {
  const day = formatDayMonthYear(postedDate || null);
  return clock ? `${day} ${clock}` : day;
}

export function ledgerKey(row: Pick<LedgerSortRow, 'posted_account' | 'product'>): string {
  return `${row.posted_account}\t${row.product || ''}`;
}

export function signedDelta(row: Pick<LedgerSortRow, 'debit_amount' | 'credit_amount' | 'fee'>): number {
  return Number(row.credit_amount || 0) - Number(row.debit_amount || 0) - Number(row.fee || 0);
}

export function nextFollowsPrevious(previous: LedgerSortRow, next: LedgerSortRow): boolean {
  if (previous.balance == null || next.balance == null) return false;
  const expected = Number(previous.balance) + signedDelta(next);
  return Math.abs(Number(next.balance) - expected) <= BALANCE_EPS;
}

function clockStamp(clock: string | null | undefined): number {
  if (!clock) return Number.POSITIVE_INFINITY;
  const [hours, minutes] = clock.split(':').map((part) => Number(part));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return Number.POSITIVE_INFINITY;
  return hours * 60 + minutes;
}

function isOldestFirstChain(rows: LedgerSortRow[]): boolean {
  for (let i = 1; i < rows.length; i += 1) {
    const prev = rows[i - 1];
    const next = rows[i];
    if (prev.balance == null || next.balance == null) continue;
    if (!nextFollowsPrevious(prev, next)) return false;
  }
  return true;
}

function pickNextSameDay<T extends LedgerSortRow>(previous: T | null, remaining: T[]): T {
  if (previous) {
    const followers = remaining.filter((row) => nextFollowsPrevious(previous, row));
    if (followers.length === 1) return followers[0];
    if (followers.length > 1) {
      return [...followers].sort((a, b) => {
        const clock = clockStamp(a.clock) - clockStamp(b.clock);
        return clock || a.id - b.id;
      })[0];
    }
  }
  const heads = remaining.filter((row) => {
    const incoming = remaining.some(
      (other) => other.id !== row.id && nextFollowsPrevious(other, row)
    );
    const outgoing = remaining.some(
      (other) => other.id !== row.id && nextFollowsPrevious(row, other)
    );
    return outgoing && !incoming;
  });
  const pool = heads.length ? heads : remaining;
  return [...pool].sort((a, b) => {
    const clock = clockStamp(a.clock) - clockStamp(b.clock);
    return clock || a.id - b.id;
  })[0];
}

/** Oldest-first order so each row's balance follows from the previous TX. */
export function orderLedgerOldestFirst<T extends LedgerSortRow>(rows: T[]): T[] {
  if (rows.length < 2) return [...rows];
  const byDate = new Map<string, T[]>();
  rows.forEach((row) => {
    const key = row.posted_date || '';
    const list = byDate.get(key) || [];
    list.push(row);
    byDate.set(key, list);
  });
  const dates = Array.from(byDate.keys()).sort((a, b) => a.localeCompare(b));
  const naive = dates.flatMap((date) =>
    [...(byDate.get(date) || [])].sort((a, b) => {
      const clock = clockStamp(a.clock) - clockStamp(b.clock);
      return clock || a.id - b.id;
    })
  );
  if (isOldestFirstChain(naive)) return naive;

  const ordered: T[] = [];
  let previous: T | null = null;
  dates.forEach((date) => {
    const remaining = [...(byDate.get(date) || [])];
    while (remaining.length) {
      const pick = pickNextSameDay(previous, remaining);
      remaining.splice(remaining.indexOf(pick), 1);
      ordered.push(pick);
      previous = pick;
    }
  });
  return ordered;
}

function naiveChronologicalOldestFirst<T extends LedgerSortRow>(rows: T[]): T[] {
  return [...rows].sort((left, right) => {
    const date = String(left.posted_date || '').localeCompare(String(right.posted_date || ''));
    if (date !== 0) return date;
    const clock = clockStamp(left.clock) - clockStamp(right.clock);
    return clock || left.id - right.id;
  });
}

function insertByNaiveIndex<T extends LedgerSortRow>(
  available: T[],
  row: T,
  naiveIndex: Map<number, number>
) {
  const rank = naiveIndex.get(row.id) || 0;
  const at = available.findIndex((item) => (naiveIndex.get(item.id) || 0) > rank);
  if (at === -1) available.push(row);
  else available.splice(at, 0, row);
}

/**
 * Date order across all accounts, with same-account running-balance order preserved
 * even when another account sits between those rows.
 */
export function orderChronologicalOldestFirst<T extends LedgerSortRow>(rows: T[]): T[] {
  if (rows.length < 2) return [...rows];
  const byId = new Map(rows.map((row) => [row.id, row]));
  const naive = naiveChronologicalOldestFirst(rows);
  const naiveIndex = new Map(naive.map((row, index) => [row.id, index]));
  const outgoing = new Map<number, number[]>();
  const incoming = new Map<number, number>();
  rows.forEach((row) => {
    outgoing.set(row.id, []);
    incoming.set(row.id, 0);
  });
  const byLedger = new Map<string, T[]>();
  rows.forEach((row) => {
    const key = ledgerKey(row);
    const list = byLedger.get(key) || [];
    list.push(row);
    byLedger.set(key, list);
  });
  byLedger.forEach((list) => {
    const chain = orderLedgerOldestFirst(list);
    for (let i = 0; i < chain.length - 1; i += 1) {
      const from = chain[i].id;
      const to = chain[i + 1].id;
      outgoing.get(from)?.push(to);
      incoming.set(to, (incoming.get(to) || 0) + 1);
    }
  });
  const available = rows
    .filter((row) => incoming.get(row.id) === 0)
    .sort((left, right) => (naiveIndex.get(left.id) || 0) - (naiveIndex.get(right.id) || 0));
  const ordered: T[] = [];
  const placed = new Set<number>();
  while (available.length) {
    const pick = available.shift();
    if (!pick || placed.has(pick.id)) continue;
    placed.add(pick.id);
    ordered.push(pick);
    (outgoing.get(pick.id) || []).forEach((succId) => {
      incoming.set(succId, (incoming.get(succId) || 1) - 1);
      if (incoming.get(succId) === 0) {
        const succ = byId.get(succId);
        if (succ) insertByNaiveIndex(available, succ, naiveIndex);
      }
    });
  }
  naive.forEach((row) => {
    if (!placed.has(row.id)) ordered.push(row);
  });
  return ordered;
}

/** Rank 0 = oldest across the mixed-account timeline. */
export function buildStatementRanks<T extends LedgerSortRow>(rows: T[]): Map<number, number> {
  const ranks = new Map<number, number>();
  orderChronologicalOldestFirst(rows).forEach((row, index) => {
    ranks.set(row.id, index);
  });
  return ranks;
}

export function compareLedgerDateSort<T extends LedgerSortRow>(
  left: T,
  right: T,
  direction: 'asc' | 'desc',
  ranks: Map<number, number>
): number {
  const dir = direction === 'asc' ? 1 : -1;
  const leftRank = ranks.get(left.id);
  const rightRank = ranks.get(right.id);
  if (leftRank != null && rightRank != null && leftRank !== rightRank) {
    return (leftRank - rightRank) * dir;
  }
  const date = String(left.posted_date || '').localeCompare(String(right.posted_date || ''));
  if (date !== 0) return date * dir;
  return (left.id - right.id) * dir;
}
